import moment from 'moment';
import { t } from 'i18next';
import type { GetApiServiceAdvancedReminderTemplates200TemplatesItem } from '@/shared/api/models';
import type { PostApiServiceAdvancedReminderTemplatesBodyReminderTriggerType } from '@/shared/api/models/postApiServiceAdvancedReminderTemplatesBodyReminderTriggerType';
import type { PostApiServiceAdvancedReminderTemplatesBodyAdditionalData } from '@/shared/api/models/postApiServiceAdvancedReminderTemplatesBodyAdditionalData';
import type { PostApiServiceAdvancedReminderTemplatesBodyAttachmentsAnyOfItem } from '@/shared/api/models/postApiServiceAdvancedReminderTemplatesBodyAttachmentsAnyOfItem';
import type { RowType } from '@/components/radix';
import type { ReminderTemplate, ReminderTriggerType } from './adv-reminder.types';

/** API list item or table row — fields optional so RadixTable `RowType` is accepted. */
export type ReminderTemplateListRow = {
    reminderTemplateId?: string;
    reminderTemplateName?: string;
    reminderTemplateType?: string;
    reminderTriggerType?: ReminderTriggerType | string;
    triggerTime?: string | null;
    reminderMinutes?: number;
    emailSubject?: string | null;
    emailContent?: string | null;
    smsContent?: string | null;
    isActive?: boolean;
    services?: Array<{ serviceId?: number | string; serviceName?: string }>;
    attachments?: PostApiServiceAdvancedReminderTemplatesBodyAttachmentsAnyOfItem[] | null;
    additionalData?: PostApiServiceAdvancedReminderTemplatesBodyAdditionalData;
};

export type ReminderTemplateRowInput =
    | ReminderTemplateListRow
    | GetApiServiceAdvancedReminderTemplates200TemplatesItem
    | RowType;

export const DEFAULT_REMINDER_TRIGGER_TYPE: ReminderTriggerType = 'BEFORE_BOOKING';
export const DEFAULT_TRIGGER_TIME = '08:00';

export function buildExcludeBookingStatusOptions(): Array<{ label: string; value: string }> {
    return [
        { label: t('Common.Cancelled'), value: 'CANCELLED' },
        { label: t('Calendar.AbsenceBooking'), value: 'NO-SHOW' },
        { label: t('Common.Completed'), value: 'COMPLETED' },
        { label: t('Calendar.Booked'), value: 'BOOKED' },
        { label: t('Calendar.Rescheduled'), value: 'RESCHEDULED' },
        { label: t('Common.AutoCompleted'), value: 'AUTOCOMPLETED' },
    ];
}

function parseExcludeBookingStatuses(additionalData: unknown): string[] {
    if (!additionalData || typeof additionalData !== 'object') {
        return [];
    }

    const statuses = (additionalData as Record<string, unknown>).exclude_booking_status;
    if (!Array.isArray(statuses)) {
        return [];
    }

    return statuses.filter((status): status is string => typeof status === 'string');
}

export function buildTriggerTimeOptions(): Array<{ label: string; value: string }> {
    return Array.from({ length: 24 }, (_, hour) => {
        const time = moment().startOf('day').add(hour, 'hours').format('HH:mm');
        return { label: time, value: time };
    });
}

export function normalizeTriggerTime(value: string | null | undefined): string | null {
    if (!value) {
        return null;
    }
    const parsed = moment(value, ['HH:mm', 'HH:mm:ss', 'H:mm'], true);
    return parsed.isValid() ? parsed.format('HH:mm') : null;
}

export function getReminderTriggerTypeLabel(
    triggerType: ReminderTriggerType | string | null | undefined,
): string {
    if (triggerType === 'DAY_OF_BOOKING') {
        return t('Services.ReminderTriggerDayOfBooking');
    }
    if (triggerType === 'AFTER_BOOKING') {
        return t('Services.ReminderTriggerAfterBooking');
    }
    return t('Services.ReminderTriggerBeforeBooking');
}

const EMPTY_TABLE_CELL = '—';

export function getReminderDurationColumnValue(
    triggerType: ReminderTriggerType | string | null | undefined,
    reminderMinutes: number | undefined,
    formatDuration: (minutes?: number) => string,
): string {
    if (triggerType === 'DAY_OF_BOOKING') {
        return EMPTY_TABLE_CELL;
    }

    return formatDuration(reminderMinutes);
}

export function getReminderTriggerTimeColumnValue(
    triggerType: ReminderTriggerType | string | null | undefined,
    triggerTime: string | null | undefined,
): string {
    if (triggerType !== 'DAY_OF_BOOKING') {
        return EMPTY_TABLE_CELL;
    }

    return normalizeTriggerTime(triggerTime) ?? EMPTY_TABLE_CELL;
}

export function mapApiTemplateToFormValues(row: ReminderTemplateRowInput): ReminderTemplate {
    const template = row as ReminderTemplateListRow;

    return {
        reminderTemplateName: template.reminderTemplateName ?? '',
        reminderTemplateType: template.reminderTemplateType ?? 'EMAIL_AND_SMS',
        reminderTriggerType: (template.reminderTriggerType as ReminderTriggerType) ?? DEFAULT_REMINDER_TRIGGER_TYPE,
        triggerTime: normalizeTriggerTime(template.triggerTime) ?? DEFAULT_TRIGGER_TIME,
        reminderMinutes: template.reminderMinutes ?? 5,
        emailSubject: (template.emailSubject as string) ?? '',
        emailContent: (template.emailContent as string) ?? '',
        smsContent: (template.smsContent as string) ?? '',
        isActive: template.isActive ?? true,
        reminderTemplateId: template.reminderTemplateId,
        services: template.services?.map((service) => String(service.serviceId ?? '')).filter(Boolean) || [],
        attachments: template.attachments ?? [],
        excludeBookingStatuses: parseExcludeBookingStatuses(template.additionalData),
    };
}

export function buildAdditionalDataApiFields(
    value: ReminderTemplate,
): PostApiServiceAdvancedReminderTemplatesBodyAdditionalData {
    if (value.reminderTriggerType !== 'AFTER_BOOKING') {
        return null;
    }

    return {
        exclude_booking_status: value.excludeBookingStatuses ?? [],
    };
}

export function buildReminderTemplateSharedApiFields(value: ReminderTemplate) {
    return {
        ...buildReminderTriggerApiFields(value),
        additionalData: buildAdditionalDataApiFields(value),
    };
}

export function buildReminderTriggerApiFields(value: ReminderTemplate): {
    reminderTriggerType: PostApiServiceAdvancedReminderTemplatesBodyReminderTriggerType;
    reminderMinutes: number;
    triggerTime: string | null;
} {
    const triggerType = value.reminderTriggerType ?? DEFAULT_REMINDER_TRIGGER_TYPE;
    const isDayOfBooking = triggerType === 'DAY_OF_BOOKING';
    const reminderMinutes =
        typeof value.reminderMinutes === 'string'
            ? parseInt(value.reminderMinutes, 10)
            : value.reminderMinutes;

    return {
        reminderTriggerType: triggerType,
        reminderMinutes: isDayOfBooking ? 1 : reminderMinutes,
        triggerTime: isDayOfBooking ? normalizeTriggerTime(value.triggerTime) ?? DEFAULT_TRIGGER_TIME : null,
    };
}
